import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PageShellComponent } from '@shared/components/page-shell/page-shell.component';
import { LocalizedRouterLinkPipe } from '@core/i18n/localized-router-link.pipe';
import { PerguntaService } from '@features/nuvem/pergunta.service';
import { EditorialSelectComponent } from '@features/nuvem/editorial-select.component';
import { PromoBannerComponent } from '@features/nuvem/promo-banner.component';
import { shortTitle, toQueryParams } from '@features/nuvem/nuvem.util';
import type { PerguntaParametrizadaResponse } from '@features/nuvem/models/pergunta.models';

type SortKey = 'popular' | 'alpha';

interface QuestionCard {
  /** Id da variante na listagem (único no track). */
  id: number;
  /** Id da pergunta na API — usado no path `/igualdade-racial/:slug`. */
  perguntaId: number;
  short: string;
  platform: string;
  views: number;
  queryParams: Record<string, string>;
}

const I18N = 'pages.racialEquality';

/** Filtro MIR — espelha `mir-main.component.ts` do front-17. */
function isMirQuestion(p: PerguntaParametrizadaResponse): boolean {
  const sistema = p.pergunta?.sistema?.toLowerCase() ?? '';
  return sistema.includes('igualdade') || sistema.includes('equality');
}

@Component({
  selector: 'app-igualdade-racial-page',
  standalone: true,
  imports: [
    TranslocoPipe,
    PageShellComponent,
    LocalizedRouterLinkPipe,
    RouterLink,
    EditorialSelectComponent,
    PromoBannerComponent,
  ],
  templateUrl: './igualdade-racial.page.html',
})
export class IgualdadeRacialPage {
  private readonly service = inject(PerguntaService);
  private readonly transloco = inject(TranslocoService);

  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  protected readonly i18n = I18N;
  protected readonly breadcrumb = [{ labelKey: 'nav.items.racialEquality' }];

  protected readonly sortOptions = computed(() => {
    this.lang();
    return [
      { value: 'popular', label: this.transloco.translate(`${I18N}.sortPopular`) },
      { value: 'alpha', label: this.transloco.translate(`${I18N}.sortAlpha`) },
    ];
  });

  private readonly perguntas = toSignal(this.service.getPerguntas(), {
    initialValue: [] as PerguntaParametrizadaResponse[],
  });

  private readonly mirPerguntas = computed(() =>
    this.perguntas().filter(isMirQuestion),
  );

  protected readonly loading = computed(
    () => this.perguntas().length === 0,
  );

  protected readonly query = signal('');
  protected readonly sort = signal<SortKey>('popular');

  protected readonly filtered = computed<QuestionCard[]>(() => {
    const q = this.query().trim().toLowerCase();
    let list = this.mirPerguntas().filter(
      (p) => !q || p.texto.toLowerCase().includes(q) || shortTitle(p).toLowerCase().includes(q),
    );

    if (this.sort() === 'popular') {
      list = [...list].sort(
        (a, b) => (b.pergunta.vezesUtilizada ?? 0) - (a.pergunta.vezesUtilizada ?? 0),
      );
    } else {
      list = [...list].sort((a, b) =>
        shortTitle(a).localeCompare(shortTitle(b), 'pt-BR'),
      );
    }

    return list.map((p) => ({
      id: p.id,
      perguntaId: p.pergunta.id,
      short: shortTitle(p),
      platform: p.pergunta.sistema,
      views: p.pergunta.vezesUtilizada ?? 0,
      queryParams: toQueryParams(p.parametros),
    }));
  });

  protected onSearch(value: string): void {
    this.query.set(value);
  }

  protected onSort(value: string): void {
    this.sort.set(value === 'alpha' ? 'alpha' : 'popular');
  }
}
